<script setup lang="ts">
import { ref } from "vue";
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import UserArea from "@/components/ui/UserArea.vue";
import InfiniteScroll from "@/components/ui/InfiniteScroll.vue";
import AppModal from "@/components/ui/AppModal.vue";
import { gql } from "@apollo/client/core";
import { useMutation, useQuery, useResult } from "@vue/apollo-composable";
import { dateToUserWithTime } from "@/libs/filters";
import { getMessageFromError } from "@/libs/errors";

interface AccountDeletionRequest {
  id: string;
  userId: string;
  status: string;
  reason: string | null;
  createdAt: string;
  originalEmail: string;
  originalRegNumber: string;
}

const { loading, result, fetchMore, refetch } = useQuery(
  gql`
    query PendingAccountDeletionRequests($first: Int, $after: String) {
      pendingAccountDeletionRequests(first: $first, after: $after) {
        nodes {
          id
          userId
          status
          reason
          createdAt
          originalEmail
          originalRegNumber
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  `,
  { first: 10, after: null as string | null }
);

const requests = useResult(
  result,
  [] as AccountDeletionRequest[],
  (data) => data.pendingAccountDeletionRequests.nodes
);

const loadMore = () => {
  return fetchMore({
    variables: {
      after: result.value.pendingAccountDeletionRequests.pageInfo.endCursor,
    },
    updateQuery: (previousResult, { fetchMoreResult }) => {
      const newNodes = fetchMoreResult.pendingAccountDeletionRequests.nodes;
      const pageInfo = fetchMoreResult.pendingAccountDeletionRequests.pageInfo;

      return newNodes.length
        ? {
            ...previousResult,
            pendingAccountDeletionRequests: {
              ...previousResult.pendingAccountDeletionRequests,
              nodes: [
                ...previousResult.pendingAccountDeletionRequests.nodes,
                ...newNodes,
              ],
              pageInfo,
            },
          }
        : {
            ...previousResult,
            pendingAccountDeletionRequests: {
              ...previousResult.pendingAccountDeletionRequests,
              pageInfo,
            },
          };
    },
  });
};

const { mutate: reviewRequest, loading: reviewLoading } = useMutation(
  gql`
    mutation ReviewAccountDeletionRequest(
      $input: ReviewAccountDeletionInput!
    ) {
      reviewAccountDeletionRequest(input: $input) {
        id
        status
      }
    }
  `,
  {
    fetchPolicy: "no-cache",
  }
);

const selectedRequest = ref<AccountDeletionRequest | null>(null);
const showApproveModal = ref(false);
const showRejectModal = ref(false);
const adminNotes = ref("");
const actionError = ref("");

const formatRegNumber = (value: string): string => {
  const clean = value.replace(/\D/g, "");
  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (clean.length === 14) {
    return clean.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }
  return value;
};

const truncateText = (text: string | null, maxLength: number): string => {
  if (!text) return "-";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const openApproveModal = (request: AccountDeletionRequest) => {
  selectedRequest.value = request;
  adminNotes.value = "";
  actionError.value = "";
  showApproveModal.value = true;
};

const openRejectModal = (request: AccountDeletionRequest) => {
  selectedRequest.value = request;
  adminNotes.value = "";
  actionError.value = "";
  showRejectModal.value = true;
};

const closeModals = () => {
  showApproveModal.value = false;
  showRejectModal.value = false;
  selectedRequest.value = null;
  adminNotes.value = "";
  actionError.value = "";
};

const confirmApprove = async () => {
  if (!selectedRequest.value || reviewLoading.value) return;

  actionError.value = "";

  try {
    await reviewRequest({
      input: {
        requestId: selectedRequest.value.id,
        approved: true,
        adminNotes: adminNotes.value.trim() || null,
      },
    });
    closeModals();
    refetch();
  } catch (err) {
    actionError.value = getMessageFromError(err);
  }
};

const confirmReject = async () => {
  if (!selectedRequest.value || reviewLoading.value) return;

  if (!adminNotes.value.trim()) {
    actionError.value = "Por favor, informe o motivo da rejeição.";
    return;
  }

  actionError.value = "";

  try {
    await reviewRequest({
      input: {
        requestId: selectedRequest.value.id,
        approved: false,
        adminNotes: adminNotes.value.trim(),
      },
    });
    closeModals();
    refetch();
  } catch (err) {
    actionError.value = getMessageFromError(err);
  }
};
</script>

<template>
  <DefaultLayout>
    <article class="container framework-slot">
      <UserArea title="Solicitações de Exclusão de Conta">
        <div v-if="loading" class="row">
          <div class="col-md-12 mb-3">CARREGANDO DADOS...</div>
        </div>
        <div v-else-if="!requests || requests.length === 0" class="row">
          <div class="col-md-12 mb-3">
            <p class="text-muted">Nenhuma solicitação pendente.</p>
          </div>
        </div>
        <div v-else>
          <InfiniteScroll
            :handler="loadMore"
            :should-handle="
              result?.pendingAccountDeletionRequests?.pageInfo?.hasNextPage
            "
          >
            <div class="table-container">
              <table class="table table-dark table-striped">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>E-mail</th>
                    <th>CPF/CNPJ</th>
                    <th>Motivo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="request of requests" :key="request.id">
                    <td>{{ dateToUserWithTime(request.createdAt) }}</td>
                    <td>{{ request.originalEmail }}</td>
                    <td>{{ formatRegNumber(request.originalRegNumber) }}</td>
                    <td :title="request.reason || undefined">
                      {{ truncateText(request.reason, 30) }}
                    </td>
                    <td class="actions-cell">
                      <button
                        class="btn btn-sm btn-success me-1"
                        @click="openApproveModal(request)"
                        :disabled="reviewLoading"
                      >
                        Aprovar
                      </button>
                      <button
                        class="btn btn-sm btn-danger"
                        @click="openRejectModal(request)"
                        :disabled="reviewLoading"
                      >
                        Rejeitar
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </InfiniteScroll>
        </div>
      </UserArea>
    </article>

    <!-- Modal de Aprovação -->
    <AppModal v-if="showApproveModal" @close="closeModals">
      <template #header>Confirmar Exclusão de Conta</template>
      <template #body>
        <div v-if="actionError" class="alert alert-danger mb-3">
          {{ actionError }}
        </div>
        <p class="mb-3">
          <strong>Atenção:</strong> Esta ação é irreversível. Os dados do
          usuário serão anonimizados permanentemente.
        </p>
        <p class="mb-3">
          <strong>E-mail:</strong> {{ selectedRequest?.originalEmail }}<br />
          <strong>CPF/CNPJ:</strong>
          {{ formatRegNumber(selectedRequest?.originalRegNumber || "") }}
        </p>
        <div class="form-group">
          <label for="approve-notes" class="form-label">
            Observações (opcional)
          </label>
          <textarea
            id="approve-notes"
            class="form-control"
            v-model="adminNotes"
            rows="3"
            placeholder="Adicione observações se necessário..."
          ></textarea>
        </div>
      </template>
      <template #footer>
        <button
          class="btn btn-secondary me-2"
          @click="closeModals"
          :disabled="reviewLoading"
        >
          Cancelar
        </button>
        <button
          class="btn btn-success"
          @click="confirmApprove"
          :disabled="reviewLoading"
        >
          <span
            v-if="reviewLoading"
            class="spinner-border spinner-border-sm me-1"
          ></span>
          Confirmar Exclusão
        </button>
      </template>
    </AppModal>

    <!-- Modal de Rejeição -->
    <AppModal v-if="showRejectModal" @close="closeModals">
      <template #header>Rejeitar Solicitação</template>
      <template #body>
        <div v-if="actionError" class="alert alert-danger mb-3">
          {{ actionError }}
        </div>
        <p class="mb-3">
          <strong>E-mail:</strong> {{ selectedRequest?.originalEmail }}<br />
          <strong>CPF/CNPJ:</strong>
          {{ formatRegNumber(selectedRequest?.originalRegNumber || "") }}
        </p>
        <div class="form-group">
          <label for="reject-notes" class="form-label">
            Motivo da rejeição <span class="text-danger">*</span>
          </label>
          <textarea
            id="reject-notes"
            class="form-control"
            v-model="adminNotes"
            rows="3"
            placeholder="Informe o motivo da rejeição..."
            required
          ></textarea>
        </div>
      </template>
      <template #footer>
        <button
          class="btn btn-secondary me-2"
          @click="closeModals"
          :disabled="reviewLoading"
        >
          Cancelar
        </button>
        <button
          class="btn btn-danger"
          @click="confirmReject"
          :disabled="reviewLoading"
        >
          <span
            v-if="reviewLoading"
            class="spinner-border spinner-border-sm me-1"
          ></span>
          Rejeitar Solicitação
        </button>
      </template>
    </AppModal>
  </DefaultLayout>
</template>

<style scoped>
.table-container {
  overflow-x: auto;
  border-radius: 0.25rem;
  margin: -10px -10px -1px;
}

.infinite-scroll {
  margin-bottom: 1rem;
}

.actions-cell {
  white-space: nowrap;
}

.btn-success {
  background-color: #198754;
  border-color: #198754;
}

.btn-success:hover:not(:disabled) {
  background-color: #157347;
  border-color: #146c43;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background-color: #bb2d3b;
  border-color: #b02a37;
}

.form-control {
  background: var(--black-eigengrau);
  border: 1px solid var(--dark-grey-alpha);
  color: var(--white-ice);
}

.form-control:focus {
  background: var(--black-eigengrau);
  border-color: var(--primary-color);
  color: var(--white-ice);
  box-shadow: 0 0 0 0.2rem rgba(166, 199, 20, 0.25);
}

.form-control::placeholder {
  color: var(--grey-color);
}

.form-label {
  color: var(--white-ice);
}

.text-muted {
  color: var(--grey-color) !important;
}

.alert-danger {
  background-color: rgba(220, 53, 69, 0.15);
  border-color: rgba(220, 53, 69, 0.3);
  color: #dc3545;
}
</style>
